/* src/components/LeadRoutingSettings.jsx
 * Workspace lead routing: off / round-robin / rules, pool membership, ordered rules.
 */

import React, { useEffect, useState } from 'react';
import { collection, doc, getDoc, getDocs, query, updateDoc, where } from 'firebase/firestore';
import { db } from '../firebase';
import { teamApi } from '../utils/api';

const MODES = [
  { id: 'off', label: 'Off', help: 'Leads stay unassigned. Immediate alerts go to you.' },
  { id: 'round_robin', label: 'Round-robin', help: 'New leads rotate fairly across the pool. Rules still win when they match.' },
  { id: 'rules', label: 'Rules only', help: 'Assign when a rule matches. Otherwise leave unassigned (you still get the immediate).' },
];

const SOURCES = [
  { id: '', label: 'Any source' },
  { id: 'form', label: 'Form' },
  { id: 'chat', label: 'Chat' },
  { id: 'sms', label: 'SMS' },
  { id: 'email', label: 'Email' },
];

function emptyRule() {
  return {
    id: `rule-${Date.now()}`,
    assignToEmail: '',
    assignToUserId: '',
    match: { formId: '', source: '', fieldContains: { field: '', needle: '' } },
  };
}

export default function LeadRoutingSettings({
  workspaceId,
  currentUser,
  members,
  isOwner,
  onError,
  onSuccess,
}) {
  const [mode, setMode] = useState('off');
  const [ownerReceivesLeads, setOwnerReceivesLeads] = useState(true);
  const [rules, setRules] = useState([]);
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!workspaceId) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [wsSnap, formsSnap] = await Promise.all([
          getDoc(doc(db, 'workspaces', workspaceId)),
          getDocs(query(collection(db, 'forms'), where('workspaceId', '==', workspaceId))),
        ]);
        if (cancelled) return;
        const routing = wsSnap.data()?.routing || {};
        setMode(routing.mode === 'round_robin' || routing.mode === 'rules' ? routing.mode : 'off');
        setOwnerReceivesLeads(routing.ownerReceivesLeads !== false);
        setRules(Array.isArray(routing.rules) ? routing.rules : []);
        setForms(formsSnap.docs.map((d) => ({ id: d.id, name: d.data().name || d.id })));
      } catch (err) {
        if (!cancelled && onError) onError(err.message || 'Failed to load routing');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [workspaceId, onError]);

  const poolMembers = (members || []).filter((m) => m.status === 'active' && (m.role === 'owner' || m.leadAccess));

  async function saveRouting(next) {
    if (!isOwner || !workspaceId) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, 'workspaces', workspaceId), {
        'routing.mode': next.mode,
        'routing.ownerReceivesLeads': next.ownerReceivesLeads,
        'routing.rules': next.rules,
      });
      if (onSuccess) onSuccess('Lead routing saved');
    } catch (err) {
      if (onError) onError(err.message || 'Failed to save routing');
    } finally {
      setSaving(false);
    }
  }

  async function handleMode(nextMode) {
    setMode(nextMode);
    await saveRouting({ mode: nextMode, ownerReceivesLeads, rules });
  }

  async function handleOwnerPool(next) {
    setOwnerReceivesLeads(next);
    await saveRouting({ mode, ownerReceivesLeads: next, rules });
  }

  async function handleMemberPool(member, next) {
    if (member.role === 'owner') {
      await handleOwnerPool(next);
      return;
    }
    try {
      await teamApi.updatePermissions({
        workspaceId,
        memberId: member.userId || member.id,
        leadAccess: member.leadAccess,
        aiReplyAccess: member.aiReplyAccess,
        receivesLeads: next,
      });
      if (onSuccess) onSuccess('Routing pool updated');
    } catch (err) {
      if (onError) onError(err.message || 'Failed to update pool');
    }
  }

  async function persistRules(nextRules) {
    setRules(nextRules);
    await saveRouting({ mode, ownerReceivesLeads, rules: nextRules });
  }

  function updateRule(idx, patch) {
    setRules((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch, match: { ...r.match, ...(patch.match || {}) } } : r)));
  }

  if (loading) {
    return (
      <div className="today-panel settings-section-panel">
        <div className="today-panel-header">
          <span className="today-panel-title">Lead routing</span>
        </div>
        <div className="settings-perms-body" style={{ color: 'var(--muted)' }}>Loading…</div>
      </div>
    );
  }

  return (
    <div className="today-panel settings-section-panel">
      <div className="today-panel-header">
        <span className="today-panel-title">Lead routing</span>
      </div>
      <div className="settings-perms-body">
        <p style={{ marginTop: 0, color: 'var(--muted)', fontSize: '0.9rem', lineHeight: 1.5 }}>
          Assignment decides who gets the immediate alert. You still get every lead in the daily digest.
        </p>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '1rem' }}>
          {MODES.map((m) => (
            <button
              key={m.id}
              type="button"
              className={mode === m.id ? 'inbox-pill-btn-primary' : 'inbox-pill-btn'}
              disabled={!isOwner || saving}
              onClick={() => handleMode(m.id)}
            >
              {m.label}
            </button>
          ))}
        </div>
        <p style={{ color: 'var(--muted)', fontSize: '0.85rem', marginTop: 0 }}>
          {MODES.find((m) => m.id === mode)?.help}
        </p>

        <h4 className="fb-subheading">Who is in the pool</h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '1.25rem' }}>
          {poolMembers.map((member) => {
            const inPool = member.role === 'owner'
              ? ownerReceivesLeads
              : member.receivesLeads !== false;
            return (
              <label key={member.userId || member.id} className="fb-checkbox-row" style={{ marginBottom: 0 }}>
                <input
                  type="checkbox"
                  checked={inPool}
                  disabled={!isOwner || saving}
                  onChange={(e) => handleMemberPool(member, e.target.checked)}
                />
                <span>
                  {member.email || member.name}
                  {member.role === 'owner' ? ' (you)' : ''}
                </span>
              </label>
            );
          })}
          {poolMembers.length === 0 && (
            <p style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
              Grant lead access to teammates before they can receive routed leads.
            </p>
          )}
        </div>

        {(mode === 'rules' || mode === 'round_robin') && (
          <>
            <h4 className="fb-subheading">Rules (first match wins)</h4>
            {rules.map((rule, idx) => (
              <div
                key={rule.id || idx}
                style={{
                  border: '1px solid var(--hairline-4)',
                  borderRadius: '10px',
                  padding: '12px',
                  marginBottom: '10px',
                  display: 'grid',
                  gap: '8px',
                }}
              >
                <select
                  value={rule.match?.formId || ''}
                  disabled={!isOwner || saving}
                  onChange={(e) => updateRule(idx, { match: { ...rule.match, formId: e.target.value } })}
                  style={{ padding: '8px', borderRadius: '8px', border: '1px solid var(--hairline-5)' }}
                >
                  <option value="">Any form</option>
                  {forms.map((f) => (
                    <option key={f.id} value={f.id}>{f.name}</option>
                  ))}
                </select>
                <select
                  value={rule.match?.source || ''}
                  disabled={!isOwner || saving}
                  onChange={(e) => updateRule(idx, { match: { ...rule.match, source: e.target.value } })}
                  style={{ padding: '8px', borderRadius: '8px', border: '1px solid var(--hairline-5)' }}
                >
                  {SOURCES.map((s) => (
                    <option key={s.id} value={s.id}>{s.label}</option>
                  ))}
                </select>
                <input
                  type="text"
                  placeholder="Field name (e.g. service)"
                  value={rule.match?.fieldContains?.field || ''}
                  disabled={!isOwner || saving}
                  onChange={(e) => updateRule(idx, {
                    match: { ...rule.match, fieldContains: { ...(rule.match?.fieldContains || {}), field: e.target.value } },
                  })}
                  style={{ padding: '8px', borderRadius: '8px', border: '1px solid var(--hairline-5)' }}
                />
                <input
                  type="text"
                  placeholder="Contains…"
                  value={rule.match?.fieldContains?.needle || ''}
                  disabled={!isOwner || saving}
                  onChange={(e) => updateRule(idx, {
                    match: { ...rule.match, fieldContains: { ...(rule.match?.fieldContains || {}), needle: e.target.value } },
                  })}
                  style={{ padding: '8px', borderRadius: '8px', border: '1px solid var(--hairline-5)' }}
                />
                <select
                  value={rule.assignToEmail || ''}
                  disabled={!isOwner || saving}
                  onChange={(e) => {
                    const email = e.target.value;
                    const member = poolMembers.find((m) => m.email === email);
                    updateRule(idx, { assignToEmail: email, assignToUserId: member?.userId || '' });
                  }}
                  style={{ padding: '8px', borderRadius: '8px', border: '1px solid var(--hairline-5)' }}
                >
                  <option value="">Assign to…</option>
                  {poolMembers.filter((m) => m.email).map((m) => (
                    <option key={m.email} value={m.email}>{m.email}</option>
                  ))}
                </select>
                {isOwner && (
                  <button
                    type="button"
                    className="inbox-pill-btn"
                    onClick={() => setRules(rules.filter((_, i) => i !== idx))}
                  >
                    Remove rule
                  </button>
                )}
              </div>
            ))}
            {isOwner && (
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button type="button" className="inbox-pill-btn" onClick={() => setRules([...rules, emptyRule()])}>
                  Add rule
                </button>
                <button type="button" className="inbox-pill-btn-primary" disabled={saving} onClick={() => persistRules(rules)}>
                  Save rules
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
